/**
 * Spacing Engine — spacing rules only.
 *
 * A density preset turns a base unit into a spacing scale plus per-zone
 * padding and stack gaps. distributeStack() is the one geometry helper:
 * it slices a zone into vertically stacked boxes with grammar-aware gaps.
 */

export const DENSITIES = {
  compact: { id: 'compact', base: 6, zonePadding: 2, stackGap: 1.5 },
  comfortable: { id: 'comfortable', base: 8, zonePadding: 3, stackGap: 2 },
  airy: { id: 'airy', base: 12, zonePadding: 3.5, stackGap: 2.5 },
};

export const listDensities = () => Object.keys(DENSITIES);

/**
 * Resolve the spacing tokens for a density.
 * Returns { base, scale: [xs..xxl], zonePadding, stackGap }.
 */
export const resolveSpacing = (density = 'comfortable') => {
  const d = DENSITIES[density];
  if (!d) throw new Error(`Unknown density "${density}"`);
  return {
    density: d.id,
    base: d.base,
    scale: {
      xs: d.base / 2,
      sm: d.base,
      md: d.base * 2,
      lg: d.base * 3,
      xl: d.base * 5,
      xxl: d.base * 8,
    },
    zonePadding: Math.round(d.base * d.zonePadding),
    stackGap: Math.round(d.base * d.stackGap),
  };
};

/** Shrink a rect by padding on all sides (never below zero size). */
export const padRect = (rect, padding) => ({
  x: rect.x + padding,
  y: rect.y + padding,
  width: Math.max(0, rect.width - padding * 2),
  height: Math.max(0, rect.height - padding * 2),
});

/**
 * Vertically stack items inside a zone.
 * items = [{ weight }] — weight is the share of remaining height (default 1).
 * Returns one rect per item, top-to-bottom, separated by `gap`.
 */
export const distributeStack = (zone, items, gap) => {
  if (!items.length) return [];
  const totalGap = gap * (items.length - 1);
  const available = Math.max(0, zone.height - totalGap);
  const totalWeight = items.reduce((s, it) => s + (it.weight || 1), 0);
  let y = zone.y;
  return items.map((it) => {
    const height = Math.round(available * ((it.weight || 1) / totalWeight));
    const rect = { x: zone.x, y: Math.round(y), width: zone.width, height };
    y += height + gap;
    return rect;
  });
};
